import React, { useEffect, useMemo, useState } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableCell } from '../ui/table';
import { SortableHead, TablePagination } from './TableControls';
import { compareNumeric, periodLabel } from './performanceData';

const TABLE_PAGE_SIZE = 25;

function sortRows(rows, sortCol, sortDir, valueOf) {
  return [...rows].sort((a, b) => {
    if (sortCol === 'period_start') {
      const cmp = new Date(a.period_start) - new Date(b.period_start);
      return sortDir === 'asc' ? cmp : -cmp;
    }
    return compareNumeric(valueOf(a), valueOf(b), sortDir);
  });
}

function GameHeadLabel({ game }) {
  return (
    <span className="inline-flex items-center gap-1.5 max-w-[160px]">
      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: game.color }} />
      <span className="truncate">{game.title}</span>
    </span>
  );
}

// One game: every metric as a column. Several games: the selected metric only,
// one column per game — "a / b / c / d" cells stop being readable past two.
// Sort columns are either 'period_start', a metric key (single game) or a
// game key (compare).
export default function PerformanceTable({ rows, games, metrics, metric, bucket }) {
  const isComparing = games.length > 1;
  const [sortCol, setSortCol] = useState('period_start');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  // Switching between single/compare changes what the sort keys mean.
  useEffect(() => { setSortCol('period_start'); setSortDir('desc'); }, [isComparing]);

  const activeMetric = metrics.find(m => m.key === metric);
  const soleKey = games[0]?.key;

  const sorted = useMemo(() => {
    const valueOf = isComparing
      ? r => r.values[sortCol]?.[metric]
      : r => r.values[soleKey]?.[sortCol];
    return sortRows(rows, sortCol, sortDir, valueOf);
  }, [rows, sortCol, sortDir, isComparing, metric, soleKey]);

  useEffect(() => { setPage(1); }, [rows, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / TABLE_PAGE_SIZE));
  const pagedRows = sorted.slice((page - 1) * TABLE_PAGE_SIZE, page * TABLE_PAGE_SIZE);

  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  // Rows lag one render behind a selection change (the refetch starts in an
  // effect), so a just-added game may have no values yet.
  const fmtCell = (fmt, v) => (v == null ? '—' : fmt(v));
  const columns = isComparing
    ? games.map(g => ({ key: g.key, label: <GameHeadLabel game={g} />, cell: r => fmtCell(activeMetric.fmt, r.values[g.key]?.[metric]) }))
    : metrics.map(m => ({ key: m.key, label: m.label, cell: r => fmtCell(m.fmt, r.values[soleKey]?.[m.key]) }));

  return (
    <>
      {isComparing && (
        <p className="text-[11px] text-ink-dim mb-2">Showing {activeMetric.label.toLowerCase()} per game — change the metric above to compare another.</p>
      )}
      <div className="border border-white/10 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead col="period_start" label="Period" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              {columns.map(c => (
                <SortableHead key={c.key} col={c.key} label={c.label} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={1 + columns.length} className="text-center text-ink-dim text-xs py-8">
                  No data for this range
                </TableCell>
              </TableRow>
            ) : pagedRows.map(r => (
              <TableRow key={r.period_start}>
                <TableCell className="font-bold text-ink">{periodLabel(r.period_start, bucket)}</TableCell>
                {columns.map(c => (
                  <TableCell key={c.key} className="text-right whitespace-nowrap">{c.cell(r)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination page={page} totalPages={totalPages} total={sorted.length} onPageChange={setPage} noun="period" />
    </>
  );
}
